/* eslint-disable import/order */
import {
  RouteRecordRaw,
  createRouter,
  createWebHistory,
} from 'vue-router';
import {
  CONFIG,
  CONSTANTS,
  IS_DEVELOPMENT,
  hasRouteAccess,
  redirectUserAfterLogin,
} from '@/helpers';
import {
  useStore,
} from '@/store';
import Bus, {
  NOTIFICATION,
} from '@/bus';

// Auth
const AuthView = ():Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "auth" */
  '@/views/Auth/AuthView.vue'
);
const LoginView = ():Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "auth" */
  '@/views/Auth/LoginView.vue'
);
const SignupView = ():Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "auth" */
  '@/views/Auth/SignupView.vue'
);

// Admin
const AdminTemplate = ():Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin" */
  '@/views/Admin/AdminTemplate.vue'
);
const UserList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-user-list" */
  '@/views/Admin/UserList.vue'
);
const MutateUser = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-mutate-user" */
  '@/views/Admin/MutateUser.vue'
);
const TenantList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-tenant-list" */
  '@/views/Admin/TenantList.vue'
);
const MutateTenant = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-mutate-tenant" */
  '@/views/Admin/MutateTenant.vue'
);
const InstitutionList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-institution-list" */
  '@/views/Admin/InstitutionList.vue'
);
const MutateInstitution = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-mutate-institution" */
  '@/views/Admin/MutateInstitution.vue'
);
const AuditLogList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-audit-logs" */
  '@/views/Admin/AuditLogList.vue'
);
const ErrorDashboard = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-error-dashboard" */
  '@/views/Admin/ErrorDashboard.vue'
);
const JobQueueList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-job-queue" */
  '@/views/Admin/JobQueueList.vue'
);
// Individual
const UserView = ():Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "individual/user-view" */
  '@/views/Individual/UserView.vue'
);
const AccountView = ():Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "individual/user-account-view" */
  '@/views/Individual/AccountView.vue'
);

const isGuest = {
  meta: {
    isGuest: true,
  },
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/auth',
    alias: '/',
    name: 'Auth',
    component: AuthView,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: LoginView,
        meta: {
          title: 'Login',
          ...isGuest.meta,
        },
      },
      {
        path: 'signup',
        name: 'Signup',
        component: SignupView,
        meta: {
          title: 'Tenant Admin Signup',
          ...isGuest.meta,
        },
      },
    ],
  },
  {
    path: '/user',
    name: 'User',
    component: UserView,
    children: [
      {
        path: 'account/view',
        name: 'AccountView',
        component: AccountView,
        meta: {
          title: 'Account View',
        },
      },
    ],
  },
  {
    path: '/admin',
    name: 'AdminTemplate',
    component: AdminTemplate,
    children: [
      {
        path: 'user-list',
        name: 'AdminList',
        component: UserList,
        meta: {
          title: 'Admin List',
        },
      },
      {
        path: 'user/add/:id',
        alias: 'user/edit/:id',
        name: 'MutateUser',
        component: MutateUser,
        meta: {
          title: 'Add/Edit Admin',
        },
      },
      {
        path: 'tenant-admin-list',
        name: 'TenantAdminList',
        component: TenantList,
        meta: {
          title: 'Tenant Admin List',
        },
      },
      {
        path: 'tenant-admin/add/:id',
        alias: 'tenant-admin/edit/:id',
        name: 'MutateTenantAdmin',
        component: MutateTenant,
        meta: {
          title: 'Add/Edit Tenant Admin',
        },
      },
      {
        path: 'institution-list',
        name: 'InstitutionList',
        component: InstitutionList,
        meta: {
          title: 'Tenant List',
        },
      },
      {
        path: 'institution/add/:id',
        alias: 'institution/edit/:id',
        name: 'MutateInstitution',
        component: MutateInstitution,
        meta: {
          title: 'Add/Edit Institution',
        },
      },
      {
        path: 'audit-logs',
        name: 'AuditLogList',
        component: AuditLogList,
        meta: {
          title: 'Audit Logs',
        },
      },
      {
        path: 'error-dashboard',
        name: 'ErrorDashboard',
        component: ErrorDashboard,
        meta: {
          title: 'Error Dashboard',
        },
      },
      {
        path: 'job-queue',
        name: 'JobQueueList',
        component: JobQueueList,
        meta: {
          title: 'Job Queue',
        },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import(/* webpackChunkName: "admin-settings" */ '../views/Admin/MutateSettings.vue'),
        meta: {
          title: 'Settings',
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  useStore.app.prevRoutePath = from.fullPath;
  useStore.app.isLoading = true;

  // ensuring user load from local storage attempted before routing
  await useStore.app.init();
  next();
});

router.afterEach((to, from, failure) => {
  useStore.app.isLoading = false;
});

router.beforeResolve((to, from, next) => {
  const { user } = useStore.auth;

  const titleSuffix = user && IS_DEVELOPMENT
    ? ` | ${user.email}`
    : '';
  if (!to.meta || !to.meta.title) {
    document.title = `${CONFIG.appName}${titleSuffix}`;
  } else {
    document.title = `${to.meta.title}${titleSuffix}`;
  }

  // Redirect unauthenticated users to the login page
  if (!user) {
    // Allow access to guest routes (login, signup, etc.)
    if (to.meta?.isGuest) {
      next();
      return;
    }
    next(CONSTANTS.ROUTES.GUEST_HOME);
    return;
  }

  const hasAccess = hasRouteAccess(to.path, user.role);
  if (!hasAccess) {
    Bus.emit(NOTIFICATION.INFO, {
      message: `Sorry, you can't access the page ${to.path}`,
    });
  }

  if (to.path === '/' || !hasAccess) {
    next(redirectUserAfterLogin(user).route);
    return;
  }

  // Allow authenticated users to proceed
  next();
});

export { routes };

export default router;
